import { FC, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { AlexandriaCandidate } from 'api/models/AlexandriaCandidate';
import { useAlexandriaCompanySearch } from 'services/hooks/dailyNav';
import { AlexandriaCompanyPickerProps } from './AlexandriaCompanyPicker.props';

const AlexandriaCompanyPicker: FC<AlexandriaCompanyPickerProps> = ({
  firmId,
  companyId,
  selectedAlexandriaId,
  onChange,
  disabled,
}) => {
  // Undefined = search with the company's own name/website (the backend default).
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [submittedTerm, setSubmittedTerm] = useState<string | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(!selectedAlexandriaId);

  const {
    data: searchData,
    isFetching: searchLoading,
    isError: searchFailed,
  } = useAlexandriaCompanySearch({
    firmId,
    companyId,
    name: submittedTerm,
    // Read-only viewers are allowed to search but can't act on a result, so
    // don't spend an upstream Alexandria call on them.
    enabled: showSearch && !disabled,
  });

  useEffect(() => {
    if (!selectedAlexandriaId) {
      setShowSearch(true);
    }
  }, [selectedAlexandriaId]);

  const results: AlexandriaCandidate[] = searchData?.results ?? [];
  const hasSearched = !searchLoading && searchData !== undefined;
  const noResults = hasSearched && results.length === 0;

  const handleSelect = (candidate: AlexandriaCandidate) => {
    onChange(candidate.alexandria_id);
    setShowSearch(false);
  };

  const handleRemove = () => {
    onChange(null);
    setShowSearch(true);
  };

  const handleSearch = () => setSubmittedTerm(searchTerm?.trim() || undefined);

  return (
    <Box>
      {selectedAlexandriaId ? (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* STUB: the raw id stands in for the company name, because Alexandria
                has no lookup by id yet. Once that query exists, label this with the
                name and richer identifying detail — the id is not the end state. */}
            <Chip
              id="alexandria-linked-chip"
              label={selectedAlexandriaId}
              size="small"
              onDelete={disabled ? undefined : handleRemove}
              disabled={disabled}
            />
            {!showSearch && (
              <Button
                id="alexandria-change-button"
                size="small"
                variant="text"
                onClick={() => setShowSearch(true)}
                disabled={disabled}
                sx={{ minWidth: 0 }}>
                Change
              </Button>
            )}
          </Box>
          <Typography
            id="alexandria-name-pending-note"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            Showing the Alexandria ID for now — the company name will appear here once Alexandria supports lookup by ID.
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Not linked to Alexandria.
        </Typography>
      )}

      {showSearch && (
        <>
          <TextField
            id="alexandria-search-input"
            placeholder="Search by company name…"
            size="small"
            fullWidth
            value={searchTerm ?? ''}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            disabled={disabled}
            helperText="Defaults to this company's name and website. Names are matched literally, so try a legal or alternate name if there is no hit."
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchLoading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {searchFailed && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Could not reach the market-data service. Try again in a moment.
            </Alert>
          )}

          {!searchFailed && results.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
              <List dense disablePadding>
                {results.map(candidate => (
                  <ListItemButton
                    key={candidate.alexandria_id}
                    onClick={() => handleSelect(candidate)}
                    disabled={disabled}>
                    <ListItemText primary={candidate.display_name || candidate.alexandria_id} />
                    {candidate.match_type === AlexandriaCandidate.match_type.DOMAIN && (
                      <Chip label="domain match" size="small" color="info" variant="outlined" />
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          {!searchFailed && noResults && (
            <Alert severity="info" sx={{ mt: 1 }}>
              No match in Alexandria. Try the company&apos;s legal or alternate name — Alexandria matches names
              literally and does not infer aliases.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default AlexandriaCompanyPicker;
